import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  confirmStillLooking,
  getCheckIns,
} from "@/services/closeOutLoopService";
import moment from "moment";
import { formatEndDate } from "@/components/WantAdExpiryNote";
import CloseOutSheet from "@/components/CloseOutSheet";
import "./CheckInCard.scss";

export const CHECK_INS_KEY = "wantAdCheckIns";

const formatDate = (value) => {
  if (!value) return null;
  return formatEndDate(String(value).slice(0, 10));
};

const relativeAgo = (days) => (days === 1 ? "1 day ago" : `${days} days ago`);

// anchorOn is the anchor timestamp. The check-ins API does not send it yet, so
// today this falls back to the relative wording; once it does, the date shows.
// daysSinceConfirmed is rounded down, so a date rebuilt from it can be a day
// late; without anchorOn keep the relative wording rather than show a wrong date.
const sinceLabel = (item) => {
  const anchor = item.anchorOn ? moment.utc(item.anchorOn) : null;
  if (anchor && anchor.isValid()) return anchor.local().format("MMM D");
  return relativeAgo(Number(item.daysSinceConfirmed) || 0);
};

export const metaLine = (item) => {
  if (item.kind === "expiry" && item.expiryDate) {
    return item.expired
      ? `Still active, end date was ${formatDate(item.expiryDate)}`
      : `Expires ${formatDate(item.expiryDate)}`;
  }
  const on = sinceLabel(item);
  if (item.anchorKind === "confirmed") return `Confirmed ${on}`;
  if (item.anchorKind === "updated") return `Edited ${on}`;
  return `Published ${on}`;
};

export const keptCurrentNote = (result) =>
  result?.expiryDate
    ? `Active until ${formatDate(result.expiryDate)}.`
    : "Active.";

export const checkInTitle = (item) => `Still looking for ${item.title}?`;

const Row = ({ item, onConfirm, onFound, state }) => (
  <li className="check-in-row">
    <div className="check-in-row__body">
      <p className="check-in-row__title">{checkInTitle(item)}</p>
      <p className="check-in-row__meta">
        {[[item.city, item.state].filter(Boolean).join(", "), metaLine(item)]
          .filter(Boolean)
          .join(" · ")}
      </p>
      {state?.error && (
        <p className="check-in-row__error" role="alert">
          {state.error}
        </p>
      )}
    </div>
    {state?.done ? (
      <p className="check-in-row__done" role="status">
        {state.done}
      </p>
    ) : (
      <div className="check-in-row__actions">
        <button
          type="button"
          className="check-in-row__keep"
          onClick={() => onConfirm(item)}
          disabled={state?.busy}
        >
          {state?.busy ? "Saving..." : "Keep Active"}
        </button>
        <button
          type="button"
          className="check-in-row__link"
          onClick={() => onFound(item)}
          disabled={state?.busy}
        >
          Mark Closed
        </button>
      </div>
    )}
  </li>
);

const DISMISS_KEY = "check-in-dismissed-ids";

const readDismissed = () => {
  try {
    const value = JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]");
    return Array.isArray(value) ? value.map(Number) : [];
  } catch (error) {
    return [];
  }
};

const writeDismissed = (ids) => {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(ids));
  } catch (error) {
    return false;
  }
  return true;
};

export const isDismissed = (items, dismissedIds) =>
  items.length > 0 && items.every((item) => dismissedIds.includes(Number(item.wantAdId)));

export const useCheckIns = (enabled = true) =>
  useQuery({ queryKey: [CHECK_INS_KEY], queryFn: getCheckIns, enabled });

const CheckInCard = ({ onChanged, onDelete }) => {
  const queryClient = useQueryClient();
  const [rowState, setRowState] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [closeOut, setCloseOut] = useState(null);
  const [dismissedIds, setDismissedIds] = useState(readDismissed);
  const query = useCheckIns();

  const setRow = (id, value) =>
    setRowState((prev) => ({ ...prev, [id]: value }));

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: [CHECK_INS_KEY] });
    onChanged && onChanged();
  };

  const confirm = async (item) => {
    setRow(item.wantAdId, { busy: true });
    try {
      const result = await confirmStillLooking(item.wantAdId);
      setRow(item.wantAdId, { done: keptCurrentNote(result) });
      onChanged && onChanged();
    } catch (e) {
      setRow(item.wantAdId, { error: e.message });
    }
  };

  const items = query.data?.items || [];
  const showCard =
    !query.isLoading &&
    !query.isError &&
    items.length > 0 &&
    (expanded || !isDismissed(items, dismissedIds));

  const dismiss = () => {
    const ids = items.map((item) => Number(item.wantAdId));
    writeDismissed(ids);
    setDismissedIds(ids);
    setExpanded(false);
  };
  const allThirtyDay = items.every((item) => item.kind === "thirty_days");
  const summary =
    items.length === 1
      ? `${items[0].title} ${allThirtyDay ? "has been active for over 30 days" : "needs a status check"}.`
      : `${items.length} Buyer Want-Listings ${allThirtyDay ? "have been active for over 30 days" : "need a status check"}.`;

  // The sheet stays mounted even when the card hides (the last check-in was
  // closed out and the refetch came back empty), so the "next buyer" step survives.
  const sheet = (
    <CloseOutSheet
      show={!!closeOut}
      wantAd={closeOut}
      onHide={() => setCloseOut(null)}
      onRemoveWithoutOutcome={(wantAdId) => {
        setCloseOut(null);
        onDelete && onDelete(wantAdId);
      }}
      onClosedOut={refresh}
    />
  );

  if (!showCard) return sheet;

  return (
    <section className="check-in-card" aria-label="Status check">
      <p className="check-in-card__line">
        <span>{summary}</span>{" "}
        <button
          type="button"
          className="check-in-row__link"
          aria-expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Hide" : "Review"}
        </button>
        <button type="button" className="check-in-card__close" aria-label="Dismiss" onClick={dismiss}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d="M2 2l10 10M12 2 2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </p>
      {expanded ? (
        <ul className="check-in-card__list">
          {items.map((item) => (
            <Row
              key={item.wantAdId}
              item={item}
              state={rowState[item.wantAdId]}
              onConfirm={confirm}
              onFound={(i) =>
                setCloseOut({ wantAdId: i.wantAdId, title: i.title })
              }
            />
          ))}
        </ul>
      ) : null}
      {sheet}
    </section>
  );
};

export default CheckInCard;
